import React from "react";
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
} from "@mui/material";

import { Lock as LockIcon, Edit as EditIcon } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import {
  DealerUserRecoveryPasswordRequest,
  DealerUsersProps,
} from "store/modules/DealerUsers/types.store";
import { formatCpf } from "utils/taxDocument";
import * as Styled from "./styles";

type TableListUsersProps = {
  data: DealerUsersProps[] | null;
  rowsPerPageOptions?:
    | (
        | number
        | {
            value: number;
            label: string;
          }
      )[]
    | undefined;
  rowsPerPage: number;
  page: number;
  onPageChange: (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    page: number,
  ) => void;
  onRowsPerPageChange?:
    | React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>
    | undefined;
  totalCount: number;
  itemActions: {
    edit: (user: DealerUsersProps) => void;
    passwordTemporary: (user: DealerUserRecoveryPasswordRequest) => void;
  };
};

const TableListUsers = ({
  data,
  rowsPerPageOptions,
  rowsPerPage,
  page,
  totalCount,
  onPageChange,
  onRowsPerPageChange,
  itemActions,
}: TableListUsersProps) => {
  const { t } = useTranslation();

  return (
    <Box sx={{ mt: 3 }}>
      <Paper sx={{ mb: 2 }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <Styled.StyledTableCell align="left">
                  {t("name")}
                </Styled.StyledTableCell>

                <Styled.StyledTableCell align="left">
                  {t("emailAndCpf")}
                </Styled.StyledTableCell>
                <Styled.StyledTableCell align="left">
                  {t("permission")}
                </Styled.StyledTableCell>
                <Styled.StyledTableCell
                  align="left"
                  width={Object.keys(itemActions).length > 1 ? "25%" : "10"}
                />
              </TableRow>
            </TableHead>
            <TableBody>
              {data &&
                data.map((user) => (
                  <TableRow key={user?.id}>
                    <Styled.StyledTableCell
                      align="left"
                      component="th"
                      scope="row"
                    >
                      {`${user?.name || ""} ${user?.surname || ""}`}
                    </Styled.StyledTableCell>

                    <Styled.StyledTableCell align="left">
                      {(user.cpf ? formatCpf(user.cpf) : user.email) ||
                        t("notInformed")}
                    </Styled.StyledTableCell>
                    <Styled.StyledTableCell align="left">
                      {t(user?.profile)}
                    </Styled.StyledTableCell>
                    <Styled.StyledTableCell align="left">
                      <Button
                        disabled={!user?.cpf && user?.profile === "deliveryman"}
                        variant="text"
                        endIcon={<LockIcon />}
                        onClick={() => {
                          return itemActions.passwordTemporary({
                            email: user.email,
                            cpf: user.cpf,
                            cellPhone: user.cellphone,
                            scope: user.profile,
                          });
                        }}
                        sx={{ color: "##D33100" }}
                      >
                        {t("temporaryPassword")}
                      </Button>
                      <Button
                        variant="text"
                        endIcon={<EditIcon />}
                        onClick={() => itemActions.edit(user)}
                        sx={{ color: "#000000" }}
                      >
                        {t("edit")}
                      </Button>
                    </Styled.StyledTableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={rowsPerPageOptions}
            component="div"
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={onPageChange}
            onRowsPerPageChange={onRowsPerPageChange}
            labelRowsPerPage={t("rowsPerPage")}
            labelDisplayedRows={({ from, count, to }) =>
              `${from}–${to} ${t("of")} ${
                count !== -1 ? count : `${t("moreThan")} ${to}`
              }`
            }
          />
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default TableListUsers;
